import { Grid, Group, Stack, useMantineTheme, Button } from '@mantine/core';
import { IconEyeOff, IconQuestionMark } from '@tabler/icons-react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';
import dt from '~/testing';
import Image from '~/domains/common/components/Image/Image';
import { Text } from '~/domains/design-system/Text';
import { useIsMobile, useIsTablet } from '~/domains/common/hooks/mediaQueries';
import { Button as DesignButton } from '~/domains/design-system/Button';
import { toSafeMarkup } from '~/domains/common/utils/toSafeMarkup';

import type { SingleSelectField, SelectFieldOption } from '../../SingleSelectField/types';
import type { MultiSelectField } from '../../MultiSelectField/types';
import { ContestManageModalsContext } from '../../../../components/Modals/ContestManageModalsContext';
import useHandleTextClick from '../../../../hooks/useHandleTextClick';

import { useStyles } from './useStyles';

function Badge({ badge }: { badge: SelectFieldOption['params']['badge'] }) {
  const theme = useMantineTheme();
  const { classes } = useStyles({});

  let Icon: React.FC<any>;
  switch (badge) {
    case 'adminOnly': {
      Icon = IconEyeOff;
      break;
    }
    default: {
      return null;
    }
  }

  return (
    <div className={classes.badge}>
      <Icon size={16} color={theme.white} />
    </div>
  );
}

export function Field<O extends SelectFieldOption>({
  option,
  field,
  isChecked,
  onChange,
}: {
  option: O;
  field: SingleSelectField | MultiSelectField;
  isChecked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const router = useRouter();
  const { classes, cx } = useStyles({
    justify: field.params?.justify,
    compact: field.params?.compact,
  });
  const inputRef = useRef<HTMLInputElement>();
  const [hasActionShowed, setHasActionShowed] = useState(false);
  const { openModal } = useContext(ContestManageModalsContext);

  const handleSubLabelClick = useHandleTextClick();

  const handleAction = useCallback(() => {
    if (option.onSelectAction?.actionType === 'openModal') {
      if (option.onSelectAction.showOnlyOnce && hasActionShowed) {
        return;
      }
      openModal(option.onSelectAction.payload);
      setHasActionShowed(true);
    }
  }, [option.onSelectAction, hasActionShowed, openModal]);

  const handleRedirect = useCallback(() => {
    if (option.redirect) {
      router.push(
        {
          query: { ...router.query, redirecting: true },
        },
        undefined,
        { shallow: true }
      );
      window.location.href = option.redirect;
    }
  }, [option.redirect]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleAction();
      handleRedirect();

      onChange(e);
    },
    [handleAction, handleRedirect, onChange]
  );

  return (
    <Tooltip
      isRendered={!!option.params?.tooltip}
      withinPortal
      position="top-start"
      key={`${option.value}`}
      label={option.params?.tooltip}
    >
      <span>
        <Button
          component="label"
          htmlFor={`${field.key}@${option.value}`}
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && inputRef?.current) {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          data-test-id={dt.contest.create.selectOptionButton}
          aria-selected={isChecked}
          aria-disabled={option.isDisabled}
          aria-valuetext={option.label}
          className={cx(classes.option, {
            isChecked,
            isDisabled: option.isDisabled,
            alignCenter: option.params?.align,
            recommendedBadge: option.isRecommended,
          })}
        >
          {option.isRecommended && (
            <Text className={classes.recommended} variant="subhead-small">
              Recommended
            </Text>
          )}
          {option.params?.badge && <Badge badge={option.params.badge} />}
          <Group /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8} align="center" noWrap>
            <input
              type={field.type === 'MultiSelectField' ? 'checkbox' : 'radio'}
              name={field.key}
              ref={inputRef}
              id={`${field.key}@${option.value}`}
              value={`${option.value}`}
              checked={isChecked}
              disabled={option.isDisabled}
              onChange={handleChange}
              className={classes.optionInput}
            />
            {option.params?.icon && (
              <Image
                fallbackComponent={<IconQuestionMark size={24} />}
                className={classes.optionIcon}
                src={option.params.icon}
                width={24}
                height={24}
                alt="icon"
              />
            )}
            <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8} justify="center">
              <Text variant="headline-medium" className={classes.label}>
                {option.params?.comingSoon && (
                  <span className={classes.comingSoonPrefix}>Coming Soon: </span>
                )}
                {option.label}
              </Text>
              {option?.subLabel && (
                <Text
                  variant="body-medium"
                  className={classes.label}
                  onClick={handleSubLabelClick}
                  dangerouslySetInnerHTML={{
                    __html: toSafeMarkup(option.subLabel),
                  }}
                />
              )}
              {option?.info && (
                <Text className={classes.label} variant="subhead-small">
                  {option.info}
                </Text>
              )}
            </Stack>
          </Group>
        </Button>
      </span>
    </Tooltip>
  );
}

function SelectFieldOptions<O extends SelectFieldOption>({
  field,
  options,
  value,
  onChange,
}: {
  field: SingleSelectField | MultiSelectField;
  options: O[];
  value: string | number | boolean | (string | number)[];
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const { classes } = useStyles({});
  const [isExpanded, setExpanded] = useState(false);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const getGridSpan = () => {
    if (isMobile) return field.params?.span?.mobile || 6;
    if (isTablet) return field.params?.span?.tablet || 6;
    return field.params?.span?.desktop || 3;
  };

  const fieldOptions = useMemo(() => {
    if (field?.slice) {
      return isExpanded ? options : options.slice(0, field.slice);
    }
    return options;
  }, [field.slice, options, isExpanded]);

  return (
    <Grid
      grow
      /* eslint-disable-line local-rules/use-theme-tokens */ gutter={isMobile ? 8 : 12}
      align="stretch"
      data-test-id={dt.contest.create.container}
    >
      {fieldOptions.map((option) => {
        const isChecked = Array.isArray(value)
          ? value.includes(option.value as string | number)
          : value === option.value;

        return (
          <Grid.Col
            data-test-id={dt.contest.create.selectOptionField}
            sx={{ div: { height: '100%' } }}
            span={getGridSpan()}
            key={`${option.value}`}
          >
            <Field onChange={onChange} option={option} field={field} isChecked={isChecked} />
          </Grid.Col>
        );
      })}
      {field?.slice && options.length > field?.slice ? (
        <DesignButton
          className={classes.viewAll}
          variant="text"
          onClick={() => setExpanded((v) => !v)}
        >
          {isExpanded ? 'Show less' : 'Show all'}
        </DesignButton>
      ) : null}
    </Grid>
  );
}

export default SelectFieldOptions;
